import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import deepmerge from '@mui/utils/deepmerge';
import Button, { LibButtonProps } from '../../components/Button';
import styles from './styles';
import SvgCheckWhite from '../../icons/CheckWhite';
import SvgCrossWhite from '../../icons/CrossWhite';
import SvgCheckGreen from '../../icons/CheckGreen';
import SvgCrossGreen from '../../icons/CrossGreen';

export interface ComparisonTableProps {
  title?: string;
  description?: string;
  columnHeadings: {
    heading: string;
    highlight: boolean;
    button: {
      label: string;
      href: string;
      variant: LibButtonProps['variant'];
      target: '_self' | '_blank';
    };
  }[];
  rows?: {
    text: string;
    column1: boolean;
    column2: boolean;
    column3: boolean;
  }[];
}

function FeatureStatusIcon({
  isCheck,
  isHighlight
}: {
  isCheck: boolean;
  isHighlight: boolean;
}) {
  if (isCheck) {
    return isHighlight ? <SvgCheckWhite /> : <SvgCheckGreen />;
  }

  return isHighlight ? (
    <SvgCrossWhite sx={styles.crossIcon} />
  ) : (
    <SvgCrossGreen sx={styles.crossIcon} />
  );
}

export default function ComparisonTable({
  columnHeadings,
  description,
  rows,
  title
}: ComparisonTableProps) {
  return (
    <Box sx={styles.container}>
      {title && (
        <Typography variant="h2" sx={styles.title}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body1" sx={styles.description}>
          {description}
        </Typography>
      )}
      <Table sx={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.textCell} />
            {columnHeadings.map((item, index) => (
              <TableCell key={index} sx={styles.normal}>
                <Typography variant="body1">{item.heading}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((row, index) => (
            <TableRow key={index} sx={styles.tableBodyRow}>
              <TableCell sx={styles.textCell}>
                <Typography variant="body1" sx={styles.headerColumnCellText}>
                  {row.text}
                </Typography>
              </TableCell>
              <TableCell
                sx={
                  columnHeadings[0].highlight ? styles.highlight : styles.normal
                }
              >
                <FeatureStatusIcon
                  isCheck={row.column1}
                  isHighlight={columnHeadings[0].highlight}
                />
              </TableCell>
              <TableCell
                sx={
                  columnHeadings[1].highlight ? styles.highlight : styles.normal
                }
              >
                <FeatureStatusIcon
                  isCheck={row.column2}
                  isHighlight={columnHeadings[1].highlight}
                />
              </TableCell>
              {columnHeadings[2] && (
                <TableCell
                  sx={
                    columnHeadings[2].highlight
                      ? styles.highlight
                      : styles.normal
                  }
                >
                  <FeatureStatusIcon
                    isCheck={row.column3}
                    isHighlight={columnHeadings[2].highlight}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
          <TableRow sx={styles.buttonsRow}>
            <TableCell sx={styles.textCell} />
            {columnHeadings.map((item, index) => (
              <TableCell
                key={index}
                sx={deepmerge(styles.desktopButtons, styles.normal)}
              >
                {item.button && (
                  <Button
                    variant={item.button.variant}
                    href={item.button.href}
                    target={item.button.target}
                    fullWidth
                  >
                    {item.button.label}
                  </Button>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
      {columnHeadings.map(
        (item, index) =>
          item.button && (
            <Button
              key={index}
              variant={item.button.variant}
              href={item.button.href}
              target={item.button.target}
              sx={styles.mobileButtons}
              fullWidth
            >
              {item.button.label}
            </Button>
          )
      )}
    </Box>
  );
}
